/* eslint-disable */
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import dayjs from "dayjs";

import { Bill } from "api";
import { Tooltip } from "components";
import { TABLE_HEADERS, EMPTY_CELL_VALUE } from "consts";
import {
  getDotColor,
  getStatusLabel,
  getUTCDateFormat,
  isNullOrUndefined,
  toUSNumber,
} from "utils";

import { DATA_ENTRIES_STATUS_PRIORITY_MAP } from "../../../consts";

const createTextCell = (
  value: string | null,
  textAlign: "left" | "center" | "right" = "center",
) => {
  if (isNullOrUndefined(value)) {
    return EMPTY_CELL_VALUE;
  }
  return (
    <Tooltip title={value}>
      <Box sx={{ textAlign }}>{value}</Box>
    </Tooltip>
  );
};

const createDateCell = (date: string) => {
  const value = date && dayjs(date).utc().format(getUTCDateFormat());
  return createTextCell(value);
};

const createUsageCell = (value: number, unit: string | null) => {
  if (isNullOrUndefined(value)) {
    return EMPTY_CELL_VALUE;
  }
  const unitText = unit === null ? "" : unit;
  const displayValue = `${toUSNumber(value)} ${unitText}`.trim();
  return (
    <Tooltip title={displayValue}>
      <Box sx={{ textAlign: "center" }}>{displayValue}</Box>
    </Tooltip>
  );
};

const createCostCell = (value: number, currency: string | null) => {
  if (isNullOrUndefined(value)) {
    return EMPTY_CELL_VALUE;
  }
  const currencyText = currency === null ? "" : currency;
  const displayValue = `${toUSNumber(value)} ${currencyText}`.trim();
  return (
    <Tooltip title={displayValue}>
      <Box>{displayValue}</Box>
    </Tooltip>
  );
};

const createStatusCell = (statusFilter: string, missingFields: any[]) => {
  const priority = DATA_ENTRIES_STATUS_PRIORITY_MAP[statusFilter] ?? 9;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        paddingLeft: "4px",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <span style={{ position: "relative" }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 5 5"
          fill={getDotColor(priority)}
          style={{ marginRight: "9px" }}
        >
          <circle cx="2.5" cy="2.5" r="2.5" />
        </svg>
        {getStatusLabel(statusFilter, missingFields)}
      </span>
    </Box>
  );
};

export const DOCUMENT_UTILITY_BILLS_COLUMNS: MRT_ColumnDef<Bill>[] = [
  {
    accessorKey: "type",
    header: "Utility type",
    enableColumnFilter: true,
    Cell: ({ cell }) => createTextCell(cell.getValue() as string | null),
  },
  {
    accessorKey: "consumptionPeriodFrom",
    header: "Start Date",
    Cell: ({ cell }) => createDateCell(cell.getValue() as string),
  },
  {
    accessorKey: "consumptionPeriodTo",
    header: "End Date",
    Cell: ({ cell }) => createDateCell(cell.getValue() as string),
  },
  {
    accessorKey: "provider",
    header: "Provider",
    Cell: ({ cell }) => createTextCell(cell.getValue() as string),
  },
  {
    accessorKey: "accountNumber",
    header: TABLE_HEADERS.accountNumber,
    Cell: ({ cell }) => createTextCell(cell.getValue() as string),
  },
  {
    accessorKey: "meterNumber",
    header: TABLE_HEADERS.meterNumber,
    Cell: ({ cell }) => createTextCell(cell.getValue() as string),
  },
  {
    accessorKey: "measuredUsageValue",
    header: "Usage",
    Cell: ({ cell, row }) =>
      createUsageCell(
        cell.getValue() as number,
        row.original?.measuredUsageUnit ?? null,
      ),
  },
  {
    accessorKey: "amount",
    header: "Cost",
    Cell: ({ cell, row }) =>
      createCostCell(cell.getValue() as number, row.original?.currency ?? null),
  },
  {
    accessorKey: "statusFilter",
    header: "Status",
    size: 300,
    Cell: ({ cell, row }) =>
      createStatusCell(
        cell.getValue() as string,
        (row.original as any)?.missingFields ?? [],
      ),
  },
];
